import React, { useMemo, useState } from 'react';
import { Button, Space, message, Tooltip } from 'antd';
import {
  WhatsAppOutlined,
  FacebookOutlined,
  TwitterOutlined,
  CopyOutlined,
  LinkedinOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { generateShareUrl } from '../../hooks/useOpenGraph';
import { shortLinkService } from '../../services/shortLink.service';

interface ShareButtonsProps {
  title?: string;
  description?: string;
  url?: string;
  source?: 'whatsapp' | 'facebook' | 'twitter' | 'linkedin' | 'telegram' | 'email';
  size?: 'small' | 'text' | 'both';
  buttonType?: 'primary' | 'default' | 'dashed' | 'text' | 'link';
  channels?: Array<'whatsapp' | 'facebook' | 'twitter' | 'linkedin' | 'telegram' | 'email' | 'copy'>;
  spacing?: 'small' | 'middle' | 'large' | number;
  onShare?: (channel: string) => void;
  surveyHash?: string;
  referralCode?: string;
}

type Channel = 'whatsapp' | 'facebook' | 'twitter' | 'linkedin' | 'telegram' | 'email' | 'copy';

const CHANNEL_UTM_SOURCE: Record<Channel, string> = {
  whatsapp: 'whatsapp',
  facebook: 'facebook',
  twitter: 'twitter',
  linkedin: 'linkedin',
  telegram: 'telegram',
  email: 'email',
  copy: 'generic',
};

export const ShareButtons: React.FC<ShareButtonsProps> = ({
  title,
  description,
  url,
  source,
  size = 'text',
  buttonType = 'default',
  channels = ['whatsapp', 'facebook', 'twitter', 'copy'],
  spacing = 'middle',
  onShare,
  surveyHash,
  referralCode: refCode,
}) => {
  const [loadingChannel, setLoadingChannel] = useState<Channel | null>(null);

  const pageTitle = useMemo(() => title || document.title, [title]);
  const pageDescription = useMemo(
    () => description || document.querySelector('meta[name="description"]')?.getAttribute('content') || '',
    [description]
  );

  const normalizedBaseUrl = useMemo(() => {
    const value = url || window.location.href;
    return value.startsWith('http') ? value : new URL(value, window.location.origin).toString();
  }, [url]);

  const getShortOrFallbackUrl = async (channel: Channel): Promise<string> => {
    const utmSource = source || CHANNEL_UTM_SOURCE[channel];

    if (surveyHash) {
      try {
        return await shortLinkService.create({
          surveyHash,
          ref: refCode,
          utmSource,
          utmMedium: 'social',
          utmCampaign: 'share',
          utmContent: new Date().toISOString().split('T')[0],
        });
      } catch {
        // Fall back to long URL on API error
      }
    }

    return generateShareUrl(
      normalizedBaseUrl,
      (utmSource as any) || 'generic'
    );
  };

  const makeHandler = (channel: Channel) => async () => {
    setLoadingChannel(channel);
    try {
      const shareUrl = await getShortOrFallbackUrl(channel);

      switch (channel) {
        case 'whatsapp': {
          const text = encodeURIComponent(`${pageTitle}\n\n${pageDescription}\n\n${shareUrl}`);
          window.open(`https://wa.me/?text=${text}`, '_blank');
          break;
        }
        case 'facebook':
          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
            '_blank'
          );
          break;
        case 'twitter': {
          const text = encodeURIComponent(`${pageTitle}\n${shareUrl}`);
          window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
          break;
        }
        case 'linkedin':
          window.open(
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
            '_blank'
          );
          break;
        case 'telegram': {
          const text = encodeURIComponent(`${pageTitle} - ${pageDescription}`);
          window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${text}`, '_blank');
          break;
        }
        case 'email': {
          const subject = encodeURIComponent(pageTitle);
          const body = encodeURIComponent(`${pageDescription}\n\n${shareUrl}`);
          window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
          break;
        }
        case 'copy':
          navigator.clipboard.writeText(shareUrl);
          message.success('Link copiado para a área de transferência!');
          break;
      }

      onShare?.(channel);
    } finally {
      setLoadingChannel(null);
    }
  };

  const buttonConfigs: Record<Channel, {
    icon: React.ReactNode;
    label: string;
    color?: string;
  }> = {
    whatsapp: { icon: <WhatsAppOutlined />, label: 'WhatsApp', color: '#25D366' },
    facebook: { icon: <FacebookOutlined />, label: 'Facebook', color: '#1877F2' },
    twitter: { icon: <TwitterOutlined />, label: 'Twitter', color: '#1DA1F2' },
    linkedin: { icon: <LinkedinOutlined />, label: 'LinkedIn', color: '#0A66C2' },
    telegram: { icon: '✈️', label: 'Telegram', color: '#0088cc' },
    email: { icon: <MailOutlined />, label: 'Email' },
    copy: { icon: <CopyOutlined />, label: 'Copiar' },
  };

  const getButtonProps = (channel: Channel) => {
    const config = buttonConfigs[channel];
    const isPrimary = buttonType === 'primary';
    const isSmall = size === 'small';
    const type: 'link' | 'default' | 'text' | 'primary' | 'dashed' = isPrimary ? 'primary' : buttonType;
    const btnSize: 'large' | 'middle' | 'small' = isSmall ? 'small' : 'middle';

    return {
      key: channel,
      icon: config.icon,
      onClick: makeHandler(channel),
      loading: loadingChannel === channel,
      disabled: loadingChannel !== null && loadingChannel !== channel,
      type,
      size: btnSize,
      children: (size === 'text' || size === 'both') && !isSmall ? config.label : undefined,
      style: isPrimary && config.color ? { backgroundColor: config.color, borderColor: config.color } : undefined,
    };
  };

  return (
    <Tooltip title="Compartilhe com seus amigos">
      <Space size={spacing} wrap>
        {channels.map((channel) => (
          <Button
            {...getButtonProps(channel as Channel)}
          />
        ))}
      </Space>
    </Tooltip>
  );
};

export default ShareButtons;
